'use strict';

const libPictProvider = require('pict-provider');

const BODY_MOUNT = '#ICW-Body';
const MESSAGE_MOUNT = '#ICW-Message';
const ASSET_PICKER_MOUNT = '#ICW-Picker-IDPhysicalAsset';
const PICKER_PAGE_SIZE = 25;

/**
 * InspectionCreateWorkspace — the bespoke Inspection CREATE screen.
 *
 * Creating an inspection is not a plain row insert: an inspection is only useful once it is joined to the asset
 * being inspected, scoped to that asset's project, and carrying the document packet its inspection type calls
 * for. `BridgeDocumentManager.createInspection()` does all four (POST Inspection → POST
 * InspectionPhysicalAssetJoin → generate one Document per InspectionTypeReportNamedInstanceJoin → wire the
 * dossier's DocumentPolyJoins), and that service is already instantiated app-wide by the HeadlightPanels view.
 * So this screen is a form over that one call.
 *
 * The router bridges #/PSRS/Inspection/Create here so this shadows the generic record-set create form. That
 * matters beyond polish: the generic form renders the raw schema (a bare number box for IDInspectionType, plain
 * text boxes for the dates) and POSTs a lone Inspection row — no asset, no project, no packet. Every inspection
 * created that way is an orphan that no screen in the platform can show properly.
 */
class InspectionCreateWorkspaceProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;
	}

	get restClient()
	{
		return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
	}

	get documentManager()
	{
		return this.pict.BridgeDocumentManager || (this.pict.fable && this.pict.fable.BridgeDocumentManager);
	}

	get currentUser()
	{
		return (this.pict && this.pict.AppData && this.pict.AppData.CurrentUser) || {};
	}

	get idCustomer()
	{
		return Number(this.currentUser.CustomerID) || 0;
	}

	get state()
	{
		if (!this.pict.AppData.InspectionCreateWorkspace)
		{
			this.pict.AppData.InspectionCreateWorkspace =
			{
				Ready: false, Message: '', MessageClass: '', Saving: false,
				Fields: { IDPhysicalAsset: '', IDInspectionType: '', Name: '', Description: '', ScheduledStartDate: '', DeadlineDate: '' },
				Errors: {},
				// Resolved from the chosen asset — shown read-only, and required before save.
				AssetLabel: '', ProjectID: 0, ProjectName: '', ProjectState: 'idle',
				// Packet preview: how many documents this inspection type will generate.
				PacketCount: null,
				// Once the user edits the name by hand we stop regenerating it under them.
				NameTouched: false,
				TypeOptions: [],
			};
		}
		return this.pict.AppData.InspectionCreateWorkspace;
	}

	// --- REST helpers ---

	_get(pURL)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.getJSON(pURL, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	_esc(pValue)
	{
		return String(pValue == null ? '' : pValue).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar]));
	}

	// --- Start ---

	/**
	 * Begin a new inspection. An optional asset pre-selects and locks the subject, which is how the Asset
	 * workspace's "Schedule inspection" action arrives here.
	 *
	 * @param {Number|String} [pIDPhysicalAsset] - pre-select this asset
	 */
	async startNewAsync(pIDPhysicalAsset)
	{
		const tmpState = this.state;
		tmpState.Message = '';
		tmpState.MessageClass = '';
		tmpState.Saving = false;
		tmpState.Errors = {};
		tmpState.Fields = { IDPhysicalAsset: '', IDInspectionType: '', Name: '', Description: '', ScheduledStartDate: '', DeadlineDate: '' };
		tmpState.AssetLabel = '';
		tmpState.ProjectID = 0;
		tmpState.ProjectName = '';
		tmpState.ProjectState = 'idle';
		tmpState.PacketCount = null;
		tmpState.NameTouched = false;
		tmpState.Ready = true;

		try
		{
			const tmpTypes = await this._get('InspectionTypes/FilteredTo/FBV~Deleted~EQ~0~FSF~Name~ASC~0/0/200');
			tmpState.TypeOptions = (Array.isArray(tmpTypes) ? tmpTypes : [])
				.map((pType) => ({ IDInspectionType: pType.IDInspectionType, Name: pType.Name || `Type ${ pType.IDInspectionType }` }));
		}
		catch (pError)
		{
			tmpState.TypeOptions = [];
			tmpState.Message = `Could not load inspection types: ${ pError.message || pError }`;
			tmpState.MessageClass = 'is-warning';
		}

		// Seed a pre-selected asset BEFORE the first paint: the picker resolves its label from the bound value
		// when it mounts, so setting this afterwards would leave the control showing its placeholder even though
		// an asset is chosen.
		const tmpID = Number(pIDPhysicalAsset);
		if (tmpID > 0) { tmpState.Fields.IDPhysicalAsset = tmpID; }

		this.render();

		if (tmpID > 0)
		{
			await this.setAssetAsync(tmpID);
		}
	}

	// --- Editing ---

	setField(pKey, pValue)
	{
		this.state.Fields[pKey] = pValue;
		if (this.state.Errors[pKey]) { delete this.state.Errors[pKey]; }
		if (pKey === 'Name') { this.state.NameTouched = (String(pValue).trim() !== ''); }
		if ((pKey === 'ScheduledStartDate') && !this.state.NameTouched) { this._regenerateName(); this._patchDerived(); }
	}

	/**
	 * Choose the inspection type: refresh the generated name and preview how many documents the packet will
	 * contain, so an unseeded type is visible BEFORE the inspection is created rather than after.
	 *
	 * @param {Number|String} pIDInspectionType
	 */
	async setTypeAsync(pIDInspectionType)
	{
		const tmpState = this.state;
		tmpState.Fields.IDInspectionType = pIDInspectionType;
		delete tmpState.Errors.IDInspectionType;
		tmpState.PacketCount = null;
		if (!tmpState.NameTouched) { this._regenerateName(); }
		this._patchDerived();

		const tmpID = Number(pIDInspectionType);
		if (!tmpID) { return; }
		try
		{
			const tmpJoins = await this._get(`InspectionTypeReportNamedInstanceJoins/FilteredTo/FBV~IDInspectionType~EQ~${ tmpID }`);
			tmpState.PacketCount = Array.isArray(tmpJoins) ? tmpJoins.length : 0;
		}
		catch (pError)
		{
			tmpState.PacketCount = null;
		}
		this._patchDerived();
	}

	/**
	 * Choose the asset being inspected, then resolve its project. The project is not cosmetic: the packet
	 * generator throws without one, and that throw is swallowed by the service's own error handling — so
	 * without this resolution a user gets a successful-looking inspection with an empty packet. Save is blocked
	 * until it resolves.
	 *
	 * @param {Number|String} pIDPhysicalAsset
	 */
	async setAssetAsync(pIDPhysicalAsset)
	{
		const tmpState = this.state;
		tmpState.Fields.IDPhysicalAsset = pIDPhysicalAsset;
		delete tmpState.Errors.IDPhysicalAsset;
		tmpState.ProjectID = 0;
		tmpState.ProjectName = '';
		tmpState.AssetLabel = '';

		const tmpID = Number(pIDPhysicalAsset);
		if (!tmpID)
		{
			tmpState.ProjectState = 'idle';
			this._patchDerived();
			return;
		}

		tmpState.ProjectState = 'loading';
		this._patchDerived();

		try
		{
			const tmpAsset = await this._get(`PhysicalAsset/${ tmpID }`);
			tmpState.AssetLabel = (tmpAsset && (tmpAsset.Name || tmpAsset.ExternalSyncGUID)) ? (tmpAsset.Name || tmpAsset.ExternalSyncGUID) : `Asset ${ tmpID }`;

			const tmpJoins = await this._get(`PhysicalAssetPolyJoins/FilteredTo/FBV~IDPhysicalAsset~EQ~${ tmpID }~FBV~RemoteType~EQ~Project`);
			const tmpIDProject = (Array.isArray(tmpJoins) && tmpJoins.length) ? Number(tmpJoins[0].IDRemote) : 0;
			if (!tmpIDProject)
			{
				tmpState.ProjectState = 'missing';
			}
			else
			{
				tmpState.ProjectID = tmpIDProject;
				tmpState.ProjectState = 'ready';
				try
				{
					const tmpProject = await this._get(`Project/${ tmpIDProject }`);
					tmpState.ProjectName = (tmpProject && tmpProject.Name) ? tmpProject.Name : `Project ${ tmpIDProject }`;
				}
				catch (pError) { tmpState.ProjectName = `Project ${ tmpIDProject }`; }
			}
		}
		catch (pError)
		{
			tmpState.ProjectState = 'error';
			tmpState.ProjectName = pError.message || String(pError);
		}

		if (!tmpState.NameTouched) { this._regenerateName(); }
		this._patchDerived();
	}

	/**
	 * Compose the conventional inspection name: "<asset> <type> Inspection - <scheduled date>", matching what
	 * the bridge apps have always generated so existing lists and reports stay legible.
	 */
	_regenerateName()
	{
		const tmpState = this.state;
		const tmpType = tmpState.TypeOptions.find((pType) => (Number(pType.IDInspectionType) === Number(tmpState.Fields.IDInspectionType)));
		const tmpParts = [ tmpState.AssetLabel, tmpType ? tmpType.Name : '', 'Inspection' ].filter(Boolean);
		if (tmpParts.length < 2) { return; }
		const tmpDate = String(tmpState.Fields.ScheduledStartDate || '').trim();
		tmpState.Fields.Name = tmpDate ? `${ tmpParts.join(' ') } - ${ tmpDate }` : tmpParts.join(' ');
	}

	_validate()
	{
		const tmpState = this.state;
		const tmpErrors = {};
		if (!Number(tmpState.Fields.IDPhysicalAsset)) { tmpErrors.IDPhysicalAsset = 'Choose the asset being inspected.'; }
		if (!Number(tmpState.Fields.IDInspectionType)) { tmpErrors.IDInspectionType = 'Choose an inspection type.'; }
		if (!String(tmpState.Fields.Name || '').trim()) { tmpErrors.Name = 'Name is required.'; }
		if (Number(tmpState.Fields.IDPhysicalAsset) && (tmpState.ProjectState !== 'ready'))
		{
			tmpErrors.IDPhysicalAsset = 'This asset is not linked to a project, so its inspection documents cannot be created.';
		}
		tmpState.Errors = tmpErrors;
		return Object.keys(tmpErrors).length === 0;
	}

	// --- Save ---

	async saveAsync()
	{
		const tmpState = this.state;
		if (tmpState.Saving) { return; }
		if (!this._validate())
		{
			tmpState.Message = 'Please fix the highlighted fields.';
			tmpState.MessageClass = 'is-danger';
			this.render();
			return;
		}
		const tmpDocumentManager = this.documentManager;
		if (!tmpDocumentManager || (typeof tmpDocumentManager.createInspection !== 'function'))
		{
			tmpState.Message = 'The inspection document service is not available in this application.';
			tmpState.MessageClass = 'is-danger';
			this.render();
			return;
		}

		tmpState.Saving = true;
		tmpState.Message = 'Creating the inspection and its documents…';
		tmpState.MessageClass = 'is-info';
		this._renderMessage();

		const tmpIDAsset = Number(tmpState.Fields.IDPhysicalAsset);
		// StatusDisplay is the denormalized label the lists read; real status is driven by the dossier's
		// approval workflow once the packet exists.
		const tmpPayload =
		{
			Name: String(tmpState.Fields.Name).trim(),
			Description: String(tmpState.Fields.Description || '').trim(),
			StatusDisplay: 'Scheduled',
			IDInspectionType: Number(tmpState.Fields.IDInspectionType),
			IDProject: tmpState.ProjectID,
		};
		if (String(tmpState.Fields.ScheduledStartDate || '').trim()) { tmpPayload.ScheduledStartDate = tmpState.Fields.ScheduledStartDate; }
		if (String(tmpState.Fields.DeadlineDate || '').trim()) { tmpPayload.DeadlineDate = tmpState.Fields.DeadlineDate; }

		try
		{
			const tmpInspection = await tmpDocumentManager.createInspection(tmpIDAsset, tmpPayload);
			if (!tmpInspection || !tmpInspection.IDInspection)
			{
				throw new Error('the server rejected the new inspection (you may not have permission to create inspections).');
			}

			// The packet is generated inside a `finally` whose errors the service logs and swallows, so a failure
			// there would otherwise look like success. Read the joins back and report what actually landed.
			const tmpPacket = await this._countPacketAsync(tmpInspection.IDInspection);
			tmpState.Saving = false;
			if (tmpPacket === 0)
			{
				tmpState.Message = 'The inspection was created, but none of its documents were — opening it so you can retry the packet.';
				tmpState.MessageClass = 'is-warning';
				this._renderMessage();
			}
			this._navigateToWorkspace(tmpInspection.IDInspection);
		}
		catch (pError)
		{
			tmpState.Saving = false;
			tmpState.Message = `Could not create the inspection: ${ pError.message || pError }`;
			tmpState.MessageClass = 'is-danger';
			this.render();
		}
	}

	/**
	 * How many documents the new inspection actually ended up with.
	 *
	 * @param {Number} pIDInspection
	 * @return {Promise<Number|null>} the count, or null when it could not be read
	 */
	async _countPacketAsync(pIDInspection)
	{
		try
		{
			const tmpJoins = await this._get(`InspectionDocumentJoins/FilteredTo/FBV~IDInspection~EQ~${ pIDInspection }~FBV~Deleted~EQ~0`);
			return Array.isArray(tmpJoins) ? tmpJoins.length : null;
		}
		catch (pError) { return null; }
	}

	cancel()
	{
		if ((typeof window !== 'undefined') && window.location)
		{
			window.location.hash = '#/PSRS/Inspection/List';
		}
	}

	_navigateToWorkspace(pIDInspection)
	{
		if ((typeof window !== 'undefined') && window.location)
		{
			window.location.hash = `#/Inspection/Workspace/${ pIDInspection }`;
		}
	}

	// --- Render ---

	render()
	{
		if (this.pict.ContentAssignment.getElement(BODY_MOUNT).length === 0) { return; }
		this.pict.ContentAssignment.assignContent(BODY_MOUNT, this._bodyHTML());
		if (this.state.Ready) { this._mountAssetPicker(); }
	}

	/**
	 * Patch just the parts that react to a selection — the resolved-project line, the packet preview and the
	 * generated name. A full repaint would tear the asset picker out of the DOM and rebuild it underneath the
	 * user, closing its dropdown and re-resolving its label on every keystroke.
	 */
	_patchDerived()
	{
		const tmpState = this.state;
		if (this.pict.ContentAssignment.getElement('#ICW-ProjectNote').length > 0)
		{
			this.pict.ContentAssignment.assignContent('#ICW-ProjectNote', this._projectHTML());
		}
		if (this.pict.ContentAssignment.getElement('#ICW-PacketNote').length > 0)
		{
			this.pict.ContentAssignment.assignContent('#ICW-PacketNote', this._packetHTML());
		}
		if (typeof document !== 'undefined')
		{
			const tmpNameInput = document.getElementById('ICW-Name');
			if (tmpNameInput && !tmpState.NameTouched) { tmpNameInput.value = tmpState.Fields.Name || ''; }
		}
	}

	_mountAssetPicker()
	{
		const tmpPickerProvider = this.pict.providers['Pict-Section-Picker'];
		if (!tmpPickerProvider || (typeof tmpPickerProvider.createPicker !== 'function')) { return; }
		if (this.pict.ContentAssignment.getElement(ASSET_PICKER_MOUNT).length === 0) { return; }
		const tmpPicker = tmpPickerProvider.createPicker('ICW-Picker-IDPhysicalAsset',
			{
				DestinationAddress: ASSET_PICKER_MOUNT,
				ValueAddress: 'AppData.InspectionCreateWorkspace.Fields.IDPhysicalAsset',
				Searchable: true,
				AllowClear: true,
				ClearLabel: '(none)',
				Placeholder: 'Search a bridge or asset…',
				DataProvider: this._assetDataProvider(),
				ResolveValue: this._assetResolveValue(),
				Entity: 'PhysicalAsset',
				OnChange: (pValue) => { this.setAssetAsync(pValue); },
			});
		if (tmpPicker && (typeof tmpPicker.render === 'function')) { tmpPicker.render(); }
	}

	_assetDataProvider()
	{
		const tmpPickerProvider = this.pict.providers['Pict-Section-Picker'];
		return (pSearchTerm, pPage) =>
		{
			const tmpStart = (Number(pPage) || 0) * PICKER_PAGE_SIZE;
			let tmpFilter = `FBV~IDCustomer~EQ~${ this.idCustomer }~FBV~Deleted~EQ~0`;
			const tmpTerm = String(pSearchTerm == null ? '' : pSearchTerm).replace(/~/g, '').trim();
			if (tmpTerm && tmpPickerProvider && (typeof tmpPickerProvider.buildSearchFilter === 'function'))
			{
				tmpFilter += `~${ tmpPickerProvider.buildSearchFilter([ 'Name', 'ExternalSyncGUID', 'AssetNumber' ], tmpTerm) }`;
			}
			return this._get(`PhysicalAssets/FilteredTo/${ tmpFilter }~FSF~Name~ASC~0/${ tmpStart }/${ PICKER_PAGE_SIZE }`)
				.then((pRows) =>
				{
					const tmpList = Array.isArray(pRows) ? pRows : [];
					return {
						results: tmpList.map((pRow) => ({ Value: Number(pRow.IDPhysicalAsset), Text: pRow.Name || pRow.ExternalSyncGUID || `Asset ${ pRow.IDPhysicalAsset }`, Record: pRow })),
						hasMore: (tmpList.length >= PICKER_PAGE_SIZE)
					};
				})
				.catch(() => ({ results: [], hasMore: false }));
		};
	}

	_assetResolveValue()
	{
		return (pValue) =>
		{
			const tmpID = Number(pValue);
			if (!tmpID) { return Promise.resolve(null); }
			return this._get(`PhysicalAsset/${ tmpID }`)
				.then((pRow) => ((pRow && pRow.IDPhysicalAsset)
					? { Value: Number(pRow.IDPhysicalAsset), Text: pRow.Name || pRow.ExternalSyncGUID || `Asset ${ tmpID }` }
					: { Value: tmpID, Text: `Asset ${ tmpID }` }))
				.catch(() => ({ Value: tmpID, Text: `Asset ${ tmpID }` }));
		};
	}

	_renderMessage()
	{
		if (this.pict.ContentAssignment.getElement(MESSAGE_MOUNT).length === 0) { this.render(); return; }
		this.pict.ContentAssignment.assignContent(MESSAGE_MOUNT, this._messageHTML());
	}

	_messageHTML()
	{
		const tmpState = this.state;
		if (!tmpState.Message) { return ''; }
		return `<div class="notification ${ this._esc(tmpState.MessageClass || 'is-info') } is-soft py-2 px-3 mb-3">${ this._esc(tmpState.Message) }</div>`;
	}

	_bodyHTML()
	{
		const tmpState = this.state;
		if (!tmpState.Ready)
		{
			return `<div class="pew-empty box has-text-grey has-text-centered"><span class="pew-spinner"></span><p class="mt-3">Loading…</p></div>`;
		}
		return `<div class="pew-head">`
			+ `<div><div class="pew-eyebrow">New Inspection</div><h1 class="pew-title">Schedule an inspection</h1></div>`
			+ `<div class="pew-head-actions">`
			+ `<button class="button is-link"${ tmpState.Saving ? ' disabled' : '' } onclick="_Pict.providers.InspectionCreateWorkspace.saveAsync(); return false;"><span>Create inspection</span></button>`
			+ `<button class="button is-light" onclick="_Pict.providers.InspectionCreateWorkspace.cancel(); return false;"><span>Cancel</span></button>`
			+ `</div></div>`
			+ `<div id="ICW-Message">${ this._messageHTML() }</div>`
			+ `<div class="pew-form">`
			+ this._subjectGroupHTML()
			+ this._scheduleGroupHTML()
			+ `</div>`;
	}

	_subjectGroupHTML()
	{
		const tmpState = this.state;
		const tmpAssetError = tmpState.Errors.IDPhysicalAsset ? `<div class="pew-field-error">${ this._esc(tmpState.Errors.IDPhysicalAsset) }</div>` : '';
		const tmpTypeError = tmpState.Errors.IDInspectionType ? `<div class="pew-field-error">${ this._esc(tmpState.Errors.IDInspectionType) }</div>` : '';
		const tmpOptions = [ `<option value="">Choose a type…</option>` ].concat(tmpState.TypeOptions.map((pType) =>
			`<option value="${ pType.IDInspectionType }"${ (Number(pType.IDInspectionType) === Number(tmpState.Fields.IDInspectionType)) ? ' selected' : '' }>${ this._esc(pType.Name) }</option>`)).join('');
		return `<div class="pew-group"><div class="pew-group-head">Subject</div><div class="pew-grid">`
			+ `<div class="pew-field pew-field-wide"><label class="pew-field-label">Asset <span class="icw-req">*</span></label>`
			+ `<div id="ICW-Picker-IDPhysicalAsset" class="pew-input pew-picker-host"></div>${ tmpAssetError }`
			+ `<div id="ICW-ProjectNote" class="icw-project">${ this._projectHTML() }</div></div>`
			+ `<div class="pew-field pew-field-wide"><label class="pew-field-label">Inspection type <span class="icw-req">*</span></label>`
			+ `<div class="select is-fullwidth pew-input"><select onchange="_Pict.providers.InspectionCreateWorkspace.setTypeAsync(this.value)">${ tmpOptions }</select></div>`
			+ `${ tmpTypeError }<div id="ICW-PacketNote">${ this._packetHTML() }</div></div>`
			+ `</div></div>`;
	}

	/**
	 * The resolved project, shown because the user cannot pick it and needs to know it was found — an asset with
	 * no project is the one case that silently produces an inspection with no documents.
	 */
	_projectHTML()
	{
		const tmpState = this.state;
		if (tmpState.ProjectState === 'loading') { return `<span class="icw-note">Resolving the asset’s project…</span>`; }
		if (tmpState.ProjectState === 'ready') { return `<span class="icw-note icw-note-ok">Project: ${ this._esc(tmpState.ProjectName) }</span>`; }
		if (tmpState.ProjectState === 'missing') { return `<span class="icw-note icw-note-bad">This asset is not linked to a project. Link it on the asset’s workspace before scheduling an inspection — the inspection’s documents are created under the project.</span>`; }
		if (tmpState.ProjectState === 'error') { return `<span class="icw-note icw-note-bad">Could not resolve the asset’s project: ${ this._esc(tmpState.ProjectName) }</span>`; }
		return '';
	}

	/** Preview the packet size so an inspection type with no report templates is visible before, not after. */
	_packetHTML()
	{
		const tmpCount = this.state.PacketCount;
		if (tmpCount == null) { return ''; }
		if (tmpCount === 0)
		{
			return `<span class="icw-note icw-note-bad">This inspection type has no report documents configured, so the inspection will be created empty.</span>`;
		}
		return `<span class="icw-note icw-note-ok">Creates ${ tmpCount } document${ (tmpCount === 1) ? '' : 's' } for this inspection.</span>`;
	}

	_scheduleGroupHTML()
	{
		const tmpState = this.state;
		const tmpNameError = tmpState.Errors.Name ? `<div class="pew-field-error">${ this._esc(tmpState.Errors.Name) }</div>` : '';
		return `<div class="pew-group"><div class="pew-group-head">Schedule</div><div class="pew-grid">`
			+ `<div class="pew-field"><label class="pew-field-label">Scheduled start</label>`
			+ `<input class="input pew-input" type="date" value="${ this._esc(tmpState.Fields.ScheduledStartDate) }" onchange="_Pict.providers.InspectionCreateWorkspace.setField('ScheduledStartDate', this.value)" /></div>`
			+ `<div class="pew-field"><label class="pew-field-label">Deadline</label>`
			+ `<input class="input pew-input" type="date" value="${ this._esc(tmpState.Fields.DeadlineDate) }" onchange="_Pict.providers.InspectionCreateWorkspace.setField('DeadlineDate', this.value)" /></div>`
			+ `<div class="pew-field pew-field-wide"><label class="pew-field-label">Name <span class="icw-req">*</span></label>`
			+ `<input id="ICW-Name" class="input pew-input" type="text" value="${ this._esc(tmpState.Fields.Name) }" onchange="_Pict.providers.InspectionCreateWorkspace.setField('Name', this.value)" />`
			+ `${ tmpNameError }<span class="icw-note">Generated from the asset, type and date — edit it and it stays as you typed it.</span></div>`
			+ `<div class="pew-field pew-field-wide"><label class="pew-field-label">Description</label>`
			+ `<textarea class="textarea pew-input" onchange="_Pict.providers.InspectionCreateWorkspace.setField('Description', this.value)">${ this._esc(tmpState.Fields.Description) }</textarea></div>`
			+ `</div></div>`;
	}
}

module.exports = InspectionCreateWorkspaceProvider;

module.exports.default_configuration =
{
	ProviderIdentifier: 'InspectionCreateWorkspace',
	AutoInitialize: true,
	AutoInitializeOrdinal: 227,
};
